import { ExternalLink, Shield } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedCoverageStatusChangedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface CoverageStatusChangedEventProps {
  event: DiscriminatedCoverageStatusChangedEvent;
  matterId: string;
}

export const CoverageStatusChangedEvent: React.FC<
  CoverageStatusChangedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { coverage, old_status, new_status } = payload;

  // Get carrier name
  const carrierName = coverage.carrier?.name || 'Unknown Carrier';

  // Get policy type
  const policyType = coverage.policy_type
    ? formatPolicyType(coverage.policy_type)
    : null;

  // Get coverage category
  const category = coverage.category
    ? formatCoverageCategory(coverage.category)
    : null;

  // Get policy identifiers
  const policyNumbers = coverage.policy_identifiers
    ?.map((pi) => pi.identifier_value)
    .filter(Boolean);

  // Format statuses
  const oldStatusLabel = formatStatus(old_status);
  const newStatusLabel = formatStatus(new_status);

  // Determine badge variant based on new status
  const newStatusVariant = getStatusVariant(new_status);

  return (
    <EventContainer>
      <EventHeader
        icon={<Shield size={20} />}
        title="Coverage Status Changed"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Carrier and Policy Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{carrierName}</span>
            {policyType && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{policyType}</Badge>
              </>
            )}
            {category && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{category}</Badge>
              </>
            )}
          </div>

          {/* Policy Numbers */}
          {policyNumbers && policyNumbers.length > 0 && (
            <div className="text-xs">
              <span className="text-slate-600">Policy Numbers:</span>{' '}
              <span className="font-mono text-[11px]">
                {policyNumbers.join(', ')}
              </span>
            </div>
          )}

          {/* Status Change */}
          <div className="flex items-center gap-2 text-xs mt-2">
            <span className="text-slate-600">Status:</span>
            <Badge variant="outline" className="line-through">
              {oldStatusLabel}
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge variant={newStatusVariant}>{newStatusLabel}</Badge>
          </div>
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/coverage/${coverage.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Coverage
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatPolicyType(type: string): string {
  const types: Record<string, string> = {
    AUTO: 'Auto',
    HOMEOWNERS: 'Homeowners',
    COMMERCIAL: 'Commercial',
    HEALTH: 'Health',
    UMBRELLA: 'Umbrella',
    OTHER: 'Other',
  };
  return types[type] || type;
}

function formatCoverageCategory(category: string): string {
  const categories: Record<string, string> = {
    FIRST_PARTY: '1st Party',
    FIRST_PARTY_HEALTH: '1st Party Health',
    THIRD_PARTY: '3rd Party',
    UNCLEAR: 'Unclear',
  };
  return categories[category] || category;
}

function formatStatus(status: string): string {
  const statuses: Record<string, string> = {
    NEEDS_APPROVAL: 'Needs Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };
  return statuses[status] || status;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'APPROVED':
      return 'default'; // Will use the default green-ish style
    case 'REJECTED':
      return 'destructive';
    case 'NEEDS_APPROVAL':
      return 'secondary';
    default:
      return 'outline';
  }
}
